import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useMatch } from 'react-router';
import { match } from 'ts-pattern';

import type { BelgianVatNumber, CreditsafeStatus } from '~/api/clients/types';

import { useClientPeppolId, useCreditsafeRecord } from '~/api/clients';
import { Blocker, Button, Container, Header, Redirect, Separator } from '~/components';
import { Form } from '~/components/Form';
import { routes } from '~/providers/RouterProvider/router.routes';

import type { ContactFormType } from '../../types';

import { ContactAddressFormSection } from '../../components/ContactAddressFormSection';
import { ContactCommentsFormSection } from '../../components/ContactCommentsFormSection';
import { ContactCustomAgreementFormSection } from '../../components/ContactCustomAgreementFormSection';
import { ContactEmailsFormSection } from '../../components/ContactEmailsFormSection';
import { ContactPersonalInfoFormSection } from '../../components/ContactPersonalInfoFormSection';
import {
  OrganisationInfoAddress,
  OrganisationInfoCard,
  OrganisationInfoCreditRating,
  OrganisationInfoGeneral,
} from '../../components/OrganisationInfoCard';
import { mapContactFormDataToPayload } from '../../utils';
import { useCreateClientContext } from '../context';
import { WizardFooter } from '../WizardFooter/WizardFooter';
import { WizardSteps } from '../WizardSteps/WizardSteps';
import { containerStyle, formStyle, sectionsStyle } from './CreateContactStep.css';
import { useOrganisationAddresses, useSaveContactFormData } from './hooks';

export const CreateContactStep = () => {
  const { t } = useTranslation(['common', 'clients', 'validation']);

  const isOrganisationContactRoute = !!useMatch(routes.createClientContactStep);
  const addresses = useOrganisationAddresses();
  const { contactFormData, isLoading, onCreate, organisation } = useCreateClientContext();

  const { data: peppolInformation } = useClientPeppolId(isBelgianVatNumber(organisation?.vatNumber) ? organisation.vatNumber : null);
  const { data: creditsafeRecord, status: creditsafeStatus } = useCreditsafeRecord(
    organisation
      ? {
          countryCode: organisation.address.countryCode,
          vatNumber: organisation.vatNumber ?? null,
          companyName: organisation.companyName,
        }
      : null,
  );

  const form = useForm<ContactFormType>({
    defaultValues: {
      firstName: contactFormData?.firstName ?? '',
      lastName: contactFormData?.lastName ?? '',
      function: contactFormData?.function ?? '',
      language: contactFormData?.language ?? 'nl',
      phoneNumber: contactFormData?.phoneNumber ?? '',
      useOrganisationAddress: contactFormData?.useOrganisationAddress ?? !!isOrganisationContactRoute,
      addressIndex: contactFormData?.addressIndex ?? 0,
      address: {
        street: contactFormData?.address?.street ?? '',
        number: contactFormData?.address?.number ?? '',
        zipCode: contactFormData?.address?.zipCode ?? '',
        city: contactFormData?.address?.city ?? '',
        countryCode: contactFormData?.address?.countryCode ?? addresses.find(({ type }) => type === 'headquarters')?.countryCode ?? 'BE',
      },
      emails: contactFormData?.emails ?? [
        {
          type: 'client',
          emailAddress: '',
          enableForQuotations: true,
          enableForInvoicesAndCreditNotes: true,
          enableForPaymentReminders: true,
        },
      ],
      customAgreement: contactFormData?.customAgreement ?? false,
      comments: contactFormData?.comments ?? '',
    },
  });

  const {
    formState: { isDirty },
    getValues,
    handleSubmit,
  } = form;

  useSaveContactFormData(getValues);

  const { search } = useLocation();

  const onSubmit = handleSubmit((data) => {
    onCreate(mapContactFormDataToPayload(data, getValues('useOrganisationAddress'), addresses));
  });

  if (isOrganisationContactRoute && !organisation) {
    return <Redirect to={routes.createClient} />;
  }

  return (
    <>
      <Header.Root>
        <Header.Content
          backTo={{ pathname: isOrganisationContactRoute ? routes.createClientOrganisationStep : routes.createClient, search }}
          title={organisation?.companyName ?? t('clients:create.title')}
        />

        <Header.Actions>
          <Header.Action icon="Save" loading={isLoading} onClick={onSubmit}>
            {t('clients:create.save')}
          </Header.Action>
        </Header.Actions>
      </Header.Root>

      <Blocker
        isBlocked={({ nextLocation }) =>
          isOrganisationContactRoute ? nextLocation.pathname !== routes.createClientOrganisationStep : isDirty
        }
        message={t('clients:create.blocker')}
      />

      <Container className={containerStyle}>
        {isOrganisationContactRoute && <WizardSteps />}

        {isOrganisationContactRoute && organisation && (
          <OrganisationInfoCard>
            <OrganisationInfoGeneral
              companyName={organisation.companyName}
              otherNumber={organisation.otherNumber ?? null}
              vatNumber={organisation.vatNumber ?? null}
            />
            <OrganisationInfoAddress address={organisation.address} />

            <OrganisationInfoCreditRating
              creditsafeRecord={creditsafeRecord ?? null}
              creditsafeStatus={match(creditsafeStatus)
                .returnType<CreditsafeStatus>()
                .with('pending', () => 'NotFetched')
                .with('success', () => 'Success')
                .with('error', () => 'Error')
                .exhaustive()}
            />
          </OrganisationInfoCard>
        )}

        <Form autoComplete="off" className={formStyle} onSubmit={onSubmit}>
          <div className={sectionsStyle}>
            <ContactPersonalInfoFormSection form={form} isPrivateClient={!isOrganisationContactRoute} />

            <Separator />

            <ContactAddressFormSection addresses={addresses} form={form} />

            <Separator />

            <ContactEmailsFormSection form={form} peppol={!!peppolInformation?.peppolId} />

            {organisation && (
              <>
                <Separator />

                <ContactCustomAgreementFormSection form={form} />
              </>
            )}

            <Separator />

            <ContactCommentsFormSection form={form} />
          </div>

          <WizardFooter
            backTo={{ pathname: isOrganisationContactRoute ? routes.createClientOrganisationStep : routes.createClient, search }}
          >
            <Button icon="Save" loading={isLoading} onClick={onSubmit}>
              {t('clients:create.save')}
            </Button>
          </WizardFooter>
        </Form>
      </Container>
    </>
  );
};

const isBelgianVatNumber = (vatNumber: string | undefined): vatNumber is BelgianVatNumber => !!vatNumber?.startsWith('BE');
